import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import api from '../api';

const ClientPortalInvitationDialog = ({ open, onClose, client, onInvitationSent }) => {
  const [emails, setEmails] = useState([]);
  const [additionalEmail, setAdditionalEmail] = useState('');
  const [includePersonalizedMessage, setIncludePersonalizedMessage] = useState(false);
  const [personalizedMessage, setPersonalizedMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [companyName, setCompanyName] = useState(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user.company_name || '';
    } catch { return ''; }
  });

  useEffect(() => {
    if (open && client) {

      // Initialize with client and spouse emails
      const initialEmails = [];
      
      // Add primary client email - always show this
      let clientEmail = null;
      
      // Check primary_contact first (based on your data structure)
      if (client.primary_contact && client.primary_contact.emails && client.primary_contact.emails.length > 0) {
        clientEmail = client.primary_contact.emails[0].email;
      }
      // Fallback to direct emails array
      else if (client.emails && client.emails.length > 0) {
        clientEmail = client.emails[0].email;
      }
      // Fallback to other possible fields
      else {
        clientEmail = client.email || client.primary_email || client.work_email;
      }
      
      if (clientEmail) {
        initialEmails.push({
          email: clientEmail,
          is_spouse: false,
          is_editable: false,
          label: 'Client',
          selected: true // Default to selected
        });
      }
      
      // Add spouse email if exists
      const spouseEmail = client.spouse_email || client.primary_contact?.spouse_email;
      if (spouseEmail) {
        initialEmails.push({
          email: spouseEmail,
          is_spouse: true,
          is_editable: false,
          label: 'Spouse',
          selected: false // Default to not selected
        });
      }
      
      setEmails(initialEmails);
    }
  }, [open, client]);

  const handleAddEmail = () => {
    if (additionalEmail.trim() && !emails.some(e => e.email === additionalEmail.trim())) {
      setEmails([...emails, {
        email: additionalEmail.trim(),
        is_spouse: false,
        is_editable: true,
        label: 'Additional',
        selected: true // Default to selected for additional emails
      }]);
      setAdditionalEmail('');
    }
  };

  const handleRemoveEmail = (index) => {
    const emailToRemove = emails[index];
    if (emailToRemove.is_editable) {
      setEmails(emails.filter((_, i) => i !== index));
    }
  };

  const handleEmailChange = (index, field, value) => {
    const updatedEmails = [...emails];
    updatedEmails[index] = { ...updatedEmails[index], [field]: value };
    setEmails(updatedEmails);
  };

  const handleEmailSelection = (index, selected) => {
    const updatedEmails = [...emails];
    updatedEmails[index] = { ...updatedEmails[index], selected };
    setEmails(updatedEmails);
  };

  const handleSendInvitations = async () => {
    const selectedEmails = emails.filter(email => email.selected);
    
    if (selectedEmails.length === 0) {
      setError('Please select at least one email address to invite');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const emailData = selectedEmails.map(email => ({
        email: email.email,
        is_spouse: email.is_spouse
      }));

      const response = await api.post(`/api/clients/${client.id}/invitations/`, {
        emails: emailData
      });

      if (response.data && response.data.success) {
        setSuccess(`Invitations sent to ${response.data.data.length} recipients`);
        onInvitationSent && onInvitationSent(response.data.data);
        
        // Close dialog after 2 seconds
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError(response.data?.error || 'Failed to send invitations');
      }
    } catch (err) {
      setError('An unexpected error occurred while sending invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmails([]);
    setAdditionalEmail('');
    setIncludePersonalizedMessage(false);
    setPersonalizedMessage('');
    setError('');
    setSuccess('');
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <EmailIcon color="primary" />
          <Typography variant="h6">The Client Portal</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {companyName
            ? `You have been invited to ${companyName} client portal. `
            : ''}
          Invite {client?.first_name || 'Client'} {client?.last_name || ''} to a unique and secure portal experience.
          Here's a list of suggested people to invite. You can also invite anyone else who should have access.
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Feature Icons */}
        <Box display="flex" gap={2} mb={3} justifyContent="center">
          <Chip icon={<EmailIcon />} label="Files" variant="outlined" />
          <Chip icon={<PersonIcon />} label="Surveys" variant="outlined" />
          <Chip icon={<EmailIcon />} label="Invoices" variant="outlined" />
          <Chip icon={<SendIcon />} label="Requests" variant="outlined" />
        </Box>

        {/* Email List */}
        <Typography variant="h6" gutterBottom>
          Suggested People
        </Typography>
        
        {emails.length === 0 ? (
          <Box textAlign="center" py={2}>
            <Typography variant="body2" color="text.secondary">
              No client email found. Please add an email below.
            </Typography>
          </Box>
        ) : (
          <List>
            {emails.map((email, index) => (
            <ListItem key={index} divider>
              <ListItemIcon>
                <Checkbox
                  checked={email.selected}
                  disabled={!email.is_editable}
                  onChange={(e) => handleEmailSelection(index, e.target.checked)}
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body1">
                      {email.email}
                    </Typography>
                    <Chip 
                      label={email.label} 
                      size="small" 
                      color={email.is_spouse ? 'secondary' : 'primary'}
                    />
                  </Box>
                }
                secondary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" color="text.secondary">
                      {email.is_spouse ? 'Spouse' : 'Client'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Personal: {email.email}
                    </Typography>
                  </Box>
                }
              />
              {email.is_editable && (
                <IconButton 
                  onClick={() => handleRemoveEmail(index)}
                  color="error"
                  size="small"
                >
                  <RemoveIcon />
                </IconButton>
              )}
            </ListItem>
            ))}
          </List>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Add New Person */}
        <Typography variant="h6" gutterBottom>
          Add Another Person
        </Typography>
        
        <Box display="flex" gap={1} mb={2}>
          <TextField
            fullWidth
            label="Enter the email of a new person"
            value={additionalEmail}
            onChange={(e) => setAdditionalEmail(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddEmail()}
            size="small"
          />
          <Button
            variant="outlined"
            onClick={handleAddEmail}
            disabled={!additionalEmail.trim()}
            startIcon={<AddIcon />}
          >
            Add
          </Button>
        </Box>

        {/* Personalized Message */}
        <FormControlLabel
          control={
            <Switch
              checked={includePersonalizedMessage}
              onChange={(e) => setIncludePersonalizedMessage(e.target.checked)}
            />
          }
          label="Include a personalized message"
        />

        {includePersonalizedMessage && (
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Personalized Message"
            value={personalizedMessage}
            onChange={(e) => setPersonalizedMessage(e.target.value)}
            placeholder="Add a personal note to the invitation..."
            sx={{ mt: 2 }}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSendInvitations}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
          disabled={loading || emails.filter(e => e.selected).length === 0}
        >
          {loading ? 'Sending...' : `Send (${emails.filter(e => e.selected).length})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientPortalInvitationDialog;
